// Отмена денежной записи подотчёта: выдачи, возврата, заявки.
//
// ## Почему отмена, а не правка
//
// Правки денежной записи в системе не существует и не появится: `UPDATE` на денежных таблицах
// не выдан ни одной роли базы, `DELETE` не выдан нигде. Отменить запись можно только обратной
// записью со ссылкой на исходную, автором и основанием — тогда история остаётся целой, а итог
// сходится.
//
// ## Почему это понадобилось делать заново
//
// Маршруты отмены на сервере были с самого начала и покрыты проверками. Позвать их было НЕЧЕМ:
// у сторно возврата не существовало ни вызова, ни типа в кабинете, у сторно выдачи вызов был
// заведён и ни разу не использован ни одним экраном.
//
// Стоило это дорого и тихо. Неверно занесённый возврат на 20 000 ₽ навсегда занижал
// неподтверждённый остаток подотчётного лица — ровно то число, которое финансовый директор
// открывает каждое утро и по которому решает, кому не выдавать больше денег.
//
// ## Почему окно, а не кнопка в строке
//
// Отмена необратима: у обратной записи своей обратной нет. Значит подтверждение обязано назвать
// сумму до нажатия — это правило продукта, а не оформление. Плюс прямая просьба владельца:
// удаление и отмена всегда с подтверждением, чтобы палец на морозе не решал за человека.

import { useState } from 'react';

import { RiArrowGoBackLine } from '@remixicon/react';

import { formatRubles } from '@/shared/money/format';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';
import { TextField } from '@/shared/ui/text-field';

/** Сколько знаков основания требует сервер. Меньше — отказ, и лучше сказать об этом заранее. */
const МИНИМУМ = 10;

export interface ReversalDialogProps {
  /** Что отменяем — словом, в винительном падеже: «выдачу», «возврат», «заявку». */
  предмет: string;
  сумма: number;
  /** Отменить с основанием. Возвращает обещание: пока оно живо, кнопка занята. */
  отменить: (основание: string) => Promise<unknown>;
  ошибка: unknown;
  идёт: boolean;
}

export function ReversalDialog({ предмет, сумма, отменить, ошибка, идёт }: ReversalDialogProps) {
  const [открыто, setОткрыто] = useState(false);
  const [основание, setОснование] = useState('');
  const коротко = основание.trim().length < МИНИМУМ;

  return (
    <Modal.Root open={открыто} onOpenChange={setОткрыто}>
      <Modal.Trigger asChild>
        <Button.Root variant='neutral' mode='ghost' size='xsmall' type='button'>
          Отменить
        </Button.Root>
      </Modal.Trigger>
      <Modal.Content>
        <Modal.Header
          icon={RiArrowGoBackLine}
          title={`Отменить ${предмет} на ${formatRubles(сумма)}?`}
          description={
            `Появится обратная запись со ссылкой на эту и вашим именем. Сама запись останется ` +
            `видимой, перечёркнутой: денежную историю в системе не стирают. Отменить отмену ` +
            `нечем.`
          }
        />
        <Modal.Body>
          <TextField
            id='reversal-basis'
            label='Почему отменяете'
            required
            placeholder='например: занесли не тому человеку'
            value={основание}
            onChange={setОснование}
            hint='Останется в истории навсегда. Одного слова недостаточно.'
          />
          {ошибка !== null && ошибка !== undefined && (
            <ErrorAlert error={ошибка as never} className='mt-3' />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button.Root variant='neutral' mode='stroke' size='small' className='w-full'>
              Не отменять
            </Button.Root>
          </Modal.Close>
          <Button.Root
            variant='error'
            mode='filled'
            size='small'
            className='w-full'
            type='button'
            disabled={коротко || идёт}
            onClick={() => {
              void отменить(основание.trim()).then(() => {
                setОткрыто(false);
                setОснование('');
              });
            }}
          >
            {идёт ? 'Отменяю…' : 'Отменить запись'}
          </Button.Root>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
